<template>
  <div>
    <!-- Investment Details -->
    <div class="mb-8 lg:mb-[3.313rem]">
      <div class="mb-4">
        <h3 class="font-medium text-strongest text-[20px] lg:text-2xl">Investment Details</h3>
        <p class="font-inter font-medium text-xs lg:text-sm text-light">
          The current configuration of your investment.
        </p>
      </div>
      <div class="grid grid-cols-2 lg:grid-cols-5 gap-2 lg:gap-3">
        <div
          v-for="(metric, index) in investmentDetailMetrics"
          :key="metric.label"
          class="h-[73px] p-4 rounded-xl bg-container-bg"
          :class="
            index === investmentDetailMetrics.length - 1 ? 'col-span-2 lg:col-span-1' : 'col-span-1'
          "
        >
          <p class="text-sm font-inter font-semibold opacity-40 text-stronger">
            {{ metric.label }}
          </p>
          <p class="text-strongest font-medium text-[18px] font-inter">
            {{
              metric.formatter
                ? metric.formatter(investmentForChange[metric.field_name])
                : investmentForChange[metric.field_name]
            }}
          </p>
        </div>
      </div>
    </div>
    <!-- Change Details -->
    <div>
      <div class="mb-4 lg:mb-6">
        <h3 class="font-medium text-strongest text-[20px] lg:text-2xl">Choose new investments</h3>
        <p class="font-inter font-medium text-xs lg:text-sm text-light">
          Divide and change your investments into new Registered Offering (S1) investments. Choose
          different term lengths and interest rates.
        </p>
      </div>

      <div class="bg-container-bg rounded-xl flex flex-col gap-6">
        <!-- Header Metrics -->
        <div
          class="flex flex-col lg:flex-row justify-between border-b border-outline-dividers p-4 lg:p-6"
        >
          <div class="flex items-center gap-12 lg:gap-16">
            <div v-for="metric in changeDetailMetrics">
              <p class="text-sm font-inter font-medium text-strong mb-1">
                {{ metric.label }}
              </p>
              <p class="text-strongest font-medium text-2xl">
                {{ metric.value }}
              </p>
            </div>
          </div>
          <div
            class="mt-4 lg:mt-0 font-inter text-info font-medium text-xs flex items-start leading-[1.2rem] lg:max-w-[45%]"
          >
            <Icon
              name="Asterisk"
              class="mr-1 h-[14px] min-h-[14px] w-[14px] min-w-[14px] -skew-x-12 stroke-info mt-1"
            />
            You&apos;ll need to allocate the entire ${{
              commaSeparateThousands(
                Math.floor(changeInvestmentStore.current_investment_for_change.current_nav / 1000) *
                  1000
              )
            }}. Amounts must be in $1,000 increments. Any remainder under $1,000 will be returned to
            you.
          </div>
        </div>

        <!-- Funds Allocation Tracker + Split CTA -->
        <div class="items-center justify-between px-6 hidden lg:flex">
          <div class="flex flex-col gap-2 w-[40%]">
            <div class="flex items-center justify-between">
              <h3 class="font-semibold font-inter text-[18px] text-strongest">Funds allocation</h3>
              <p
                class="font-inter font-medium"
                :class="
                  showAllocationError && amountRemaining > 0
                    ? 'text-error'
                    : amountRemaining === 0
                      ? 'text-success'
                      : 'text-strongest'
                "
              >
                Amount remaining: ${{ commaSeparateThousands(amountRemaining) }}
              </p>
            </div>

            <!-- Progress Bar -->
            <div
              class="w-full h-2 bg-white rounded-full overflow-hidden border"
              :class="trackerBarStyles.border"
            >
              <div
                class="h-full rounded-full transition-all duration-300 ease-out"
                :class="trackerBarStyles.fill"
                :style="{ width: `${trackerPercentage}%` }"
              />
            </div>
          </div>
          <Button @click="changeInvestmentStore.addNewInvestmentForSplit" variant="primary-grey">
            <template #label>Add investment</template>
          </Button>
        </div>

        <!-- Investment Children -->
        <div class="flex flex-col gap-6 px-4 lg:px-6 pb-6">
          <div
            v-for="(child, index) in changeInvestmentStore.change_investment_children"
            :key="index"
            class="bg-bg-canvas rounded-xl p-4 flex flex-col gap-3"
          >
            <div class="flex justify-between items-center">
              <h3 class="font-bold font-inter text-sm text-strongest">
                Investment {{ index + 1 }}
              </h3>
              <Button
                v-if="changeInvestmentStore.change_investment_children.length > 1"
                @click="changeInvestmentStore.removeInvestmentSplit(index)"
                variant="paper-primary"
                size="sm"
                class="lg:hidden shrink-0"
              >
                <template #icon>
                  <Icon name="Trash2" class="h-4 w-4 text-error" />
                </template>
              </Button>
            </div>

            <div class="flex items-start justify-between gap-4">
              <!-- Inputs Row -->
              <div class="grid grid-cols-6 md:gap-x-[15px] gap-2 md:gap-y-[14px] w-full">
                <!-- Amount -->
                <div class="col-span-6 md:col-span-2">
                  <BaseInput
                    label="Amount"
                    field_name="change_investment_amount"
                    field_type="number"
                    number_type="currency"
                    v-model="child.amount"
                    variant="secondary"
                    :error_message="overAllocationErrors[index] || childErrors[index]?.amount || ''"
                    @update:modelValue="() => handleChildAmountInput(index)"
                    @blur="handleChildAmountBlur(index)"
                  />
                </div>

                <!-- Interest Paid -->
                <div class="col-span-6 md:col-span-2">
                  <BaseInput
                    label="Interest Paid"
                    field_name="change_investment_is_compounded"
                    field_type="dropdown"
                    :dropdown_options="interestPaidOptions"
                    v-model="child.is_compounded"
                    variant="secondary"
                    :error_message="
                      duplicateFieldErrors[index]?.field === 'is_compounded'
                        ? duplicateFieldErrors[index].message
                        : ''
                    "
                    dropdown_above
                    @update:modelValue="() => checkDuplicateCombo(index, 'is_compounded')"
                  />
                </div>

                <!-- Anticipated Yield -->
                <div class="col-span-6 md:col-span-2">
                  <BaseInput
                    label="Anticipated yield*"
                    field_name="change_investment_offering"
                    field_type="dropdown"
                    :dropdown_options="getOfferingOptionsForChild(index)"
                    v-model="child.offering"
                    variant="secondary"
                    :error_message="
                      duplicateFieldErrors[index]?.field === 'offering'
                        ? duplicateFieldErrors[index].message
                        : ''
                    "
                    dropdown_above
                    @update:modelValue="() => checkDuplicateCombo(index, 'offering')"
                  />
                </div>
              </div>
              <Button
                v-if="changeInvestmentStore.change_investment_children.length > 1"
                @click="changeInvestmentStore.removeInvestmentSplit(index)"
                variant="paper-primary"
                class="hidden lg:flex mt-4 shrink-0"
              >
                <template #icon>
                  <Icon name="Trash2" class="h-4 w-4 text-error" />
                </template>
              </Button>
            </div>
          </div>
        </div>
      </div>
      <div class="justify-end mt-6 hidden lg:flex">
        <Button @click="$emit('continue')" size="lg" variant="primary-secondary">
          <template #label> Continue </template>
        </Button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import { useChangeInvestmentStore, useUIStore } from "@/stores";
import { BaseInput, Icon, AsteriskNotice, Button } from "@/components";
import { commaSeparateThousands } from "@/utilities";

const changeInvestmentStore = useChangeInvestmentStore();
const UIStore = useUIStore();

const showAllocationError = ref(false);
const overAllocationErrors = ref({});
const duplicateFieldErrors = ref({});

const investmentForChange = computed(() => {
  return changeInvestmentStore.current_investment_for_change;
});

const trackerPercentage = computed(() => {
  if (changeInvestmentStore.investableAmount === 0) return 0;
  const pct =
    (changeInvestmentStore.totalChildrenAmount / changeInvestmentStore.investableAmount) * 100;
  return Math.min(pct, 100);
});

const amountRemaining = computed(() => {
  const available = Math.floor(investmentForChange.value.current_nav / 1000) * 1000;
  return Math.max(available - changeInvestmentStore.totalChildrenAmount, 0);
});

const trackerBarStyles = computed(() => {
  if (showAllocationError.value && amountRemaining.value > 0) {
    return { fill: "bg-error", border: "border-error" };
  }
  if (amountRemaining.value === 0) {
    return { fill: "bg-success", border: "border-success" };
  }
  return { fill: "bg-filter-bg-selected-chip", border: "border-radio-checkbox-outline" };
});

const handleChildAmountInput = (index) => {
  const child = changeInvestmentStore.change_investment_children[index];
  const rawValue = Number(child.amount) || 0;

  if (rawValue <= 0) {
    overAllocationErrors.value[index] = null;
    return;
  }

  const otherChildrenTotal = changeInvestmentStore.change_investment_children.reduce(
    (sum, c, i) => {
      if (i === index) return sum;
      return sum + (Number(c.amount) || 0);
    },
    0
  );

  const available = changeInvestmentStore.investableAmount;
  const maxForThisChild = available - otherChildrenTotal;

  if (rawValue > maxForThisChild) {
    const over = rawValue - maxForThisChild;
    overAllocationErrors.value[index] =
      `That's $${commaSeparateThousands(over)} more than what's left to allocate. You have $${commaSeparateThousands(Math.max(maxForThisChild, 0))} available.`;
  } else {
    overAllocationErrors.value[index] = null;
  }
};

const handleChildAmountBlur = (index) => {
  const child = changeInvestmentStore.change_investment_children[index];
  const rawValue = Number(child.amount) || 0;

  if (rawValue <= 0) {
    overAllocationErrors.value[index] = null;
    return;
  }

  const rounded = Math.ceil(rawValue / 1000) * 1000;

  const otherChildrenTotal = changeInvestmentStore.change_investment_children.reduce(
    (sum, c, i) => {
      if (i === index) return sum;
      return sum + (Number(c.amount) || 0);
    },
    0
  );

  const available = changeInvestmentStore.investableAmount;
  const maxForThisChild = available - otherChildrenTotal;

  if (rounded > maxForThisChild) {
    // Snap to max available (rounded down to nearest 1000)
    child.amount = Math.floor(maxForThisChild / 1000) * 1000;
    overAllocationErrors.value[index] = null;
  } else {
    child.amount = rounded;
    overAllocationErrors.value[index] = null;
  }
};

const checkDuplicateCombo = (index, changedField) => {
  const child = changeInvestmentStore.change_investment_children[index];

  // Clear previous duplicate error for this child
  delete duplicateFieldErrors.value[index];

  // Can't check if both fields aren't set yet
  if (!child.offering?.value || child.is_compounded?.value == null) return;

  const childKey = `${child.offering.value.bond_term}-${child.is_compounded.value}`;
  const parentKey = changeInvestmentStore.parentComboKey;
  const termYears = child.offering.value.bond_term / 12;
  const isCompounded = child.is_compounded.value;
  const interestType = isCompounded ? "At Maturity" : "Monthly";
  const oppositeType = isCompounded ? "Monthly" : "At Maturity";

  // Check against parent
  if (childKey === parentKey) {
    if (changedField === "is_compounded") {
      duplicateFieldErrors.value[index] = {
        field: "is_compounded",
        message: `"${interestType}" matches your current investment with this term. Select "${oppositeType}" or choose a different yield.`,
      };
    } else {
      duplicateFieldErrors.value[index] = {
        field: "offering",
        message: `This term is already used with ${interestType} on your current investment. Switch to ${oppositeType}, or choose a different term.`,
      };
    }
    return;
  }

  // Check against other siblings
  const siblings = changeInvestmentStore.change_investment_children;
  for (let i = 0; i < siblings.length; i++) {
    if (i === index) continue;
    const sib = siblings[i];
    if (!sib.offering?.value || sib.is_compounded?.value == null) continue;
    const sibKey = `${sib.offering.value.bond_term}-${sib.is_compounded.value}`;
    if (childKey === sibKey) {
      if (changedField === "is_compounded") {
        duplicateFieldErrors.value[index] = {
          field: "is_compounded",
          message: `"${interestType}" is already in use with this anticipated yield on Investment ${i + 1}. Select "${oppositeType}" or a different yield.`,
        };
      } else {
        duplicateFieldErrors.value[index] = {
          field: "offering",
          message: `This term is already used with ${interestType} on Investment ${i + 1}. Switch to ${oppositeType}, or choose a different term.`,
        };
      }
      return;
    }
  }
};

const childErrors = computed(() => {
  const errors = {};
  const children = changeInvestmentStore.change_investment_children;

  children.forEach((child, i) => {
    const childErrs = {};
    const amt = Number(child.amount) || 0;
    if (amt < 1000) {
      childErrs.amount = "The minimum investment per split is $1,000. Please enter $1,000 or more.";
    }
    if (Object.keys(childErrs).length > 0) {
      errors[i] = childErrs;
    }
  });

  return errors;
});

const interestPaidOptions = [
  { name: "Monthly", value: false },
  { name: "At Maturity", value: true },
];

const getTakenCombos = (excludeIndex) => {
  const taken = new Set();
  changeInvestmentStore.change_investment_children.forEach((c, i) => {
    if (i === excludeIndex) return;
    if (!c.offering?.value || c.is_compounded == null) return;
    const key = `${c.offering.value.bond_term}-${c.is_compounded.value}`;
    taken.add(key);
  });
  // Also add parent combo
  taken.add(changeInvestmentStore.parentComboKey);
  return taken;
};

// Offering options — add subtext if the term+compounded combo is already taken by either the parent or a sibling
const getOfferingOptionsForChild = (index) => {
  const child = changeInvestmentStore.change_investment_children[index];
  const takenCombos = getTakenCombos(index);

  return changeInvestmentStore.availableOfferings
    .map((o) => {
      let subtext = null;

      if (child.is_compounded != null) {
        const specificKey = `${o.bond_term}-${child.is_compounded.value}`;
        if (takenCombos.has(specificKey)) {
          const type = child.is_compounded.value ? "at maturity" : "monthly";
          subtext = `(already used with ${type})`;
        }
      } else {
        const simpleKey = `${o.bond_term}-false`;
        const compoundedKey = `${o.bond_term}-true`;
        if (takenCombos.has(simpleKey) && takenCombos.has(compoundedKey)) {
          subtext = "(already used with both payment types)";
        }
      }

      return {
        value: { ...o },
        name: `${o.interest_rate}% (${o.bond_term / 12} year term)`,
        subtext,
      };
    })
    .sort((a, b) => a.value.bond_term - b.value.bond_term);
};

// Static Data
const investmentDetailMetrics = [
  {
    field_name: "amount",
    label: "Amount",
    formatter: (value) => `$${commaSeparateThousands(value)}`,
  },
  {
    field_name: "current_nav",
    label: "Current Bond Value",
    formatter: (value) => `$${commaSeparateThousands(value.toFixed(2))}`,
  },
  { field_name: "interest_rate", label: "Interest Rate", formatter: (value) => `${value}%` },
  { field_name: "bond_term", label: "Term", formatter: (value) => `${value / 12} years` },
  {
    field_name: "is_compounded",
    label: "Interest Paid",
    formatter: (value) => (value ? "At Maturity" : "Monthly"),
  },
];

const changeDetailMetrics = computed(() => [
  // {
  //   label: "Current Bond Value",
  //   value: `$${commaSeparateThousands(investmentForChange.value.current_nav)}`,
  // },
  {
    label: "Amount Available to Split",
    value: `$${commaSeparateThousands(Math.floor(investmentForChange.value.current_nav / 1000) * 1000)}`,
  },
  {
    label: "Amount paid back",
    value: `$${changeInvestmentStore.paidBackAmount.toFixed(2)}`,
  },
]);

async function validate() {
  showAllocationError.value = amountRemaining.value > 0;

  // Re-check all children for duplicates (in case they haven't interacted)
  changeInvestmentStore.change_investment_children.forEach((_, i) => {
    if (!duplicateFieldErrors.value[i]) {
      checkDuplicateCombo(i, "offering"); // default to showing on offering
    }
  });

  const hasDuplicateErrors = Object.keys(duplicateFieldErrors.value).length > 0;
  const validation = changeInvestmentStore.splitValidation;

  if (amountRemaining.value > 0) {
    UIStore.animateNotificationAlert({
      type: "error",
      icon: "Info",
      message: `You still have $${commaSeparateThousands(amountRemaining.value)} left to allocate.`,
      does_persist: true,
    });
    return { proceed: false };
  }

  if (!validation.is_valid || hasDuplicateErrors) {
    UIStore.animateNotificationAlert({
      type: "error",
      icon: "Info",
      message: `Make sure all splits are valid before continuing.`,
      does_persist: true,
    });
    return { proceed: false };
  }

  showAllocationError.value = false;
  return { proceed: true };
}

defineExpose({ validate });
</script>
